import { isBedbankItem } from 'lib/checkout/checkoutUtils'
import { subDays } from 'lib/datetime/dateUtils'
import { min, sortBy } from 'lib/array/arrayUtils'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import { shouldShowBedbankRate } from 'lib/hotels/bedbankUtils'
import { onlyRefundableRate } from 'checkout/lib/utils/tours/accommodation'

export interface IncludedAccommodation {
  offerId: string
  rateId: string
  price: number
}

/** Display the additional accommodation cost without changing the booked price. */
export function getAccommodationUpgradePrice(price: number, includedAccommodation: IncludedAccommodation): number {
  return Math.round((price - includedAccommodation.price) * 100) / 100 || 0
}

export function getBedbankAccommodationPrice(rate: App.BedbankRate, useMemberPrice: boolean): number {
  const memberPrice = rate.totals.memberInclusive ?? 0
  const price = useMemberPrice && memberPrice > 0 ? memberPrice : rate.totals.inclusive
  return price + (rate.totals.propertyFees ?? 0)
}

export interface CruiseBundleHotelOption {
  offer: App.BedbankOffer
  rate: App.BedbankRate
  price: number
}

export function isCruiseInflowBundle(bundle: App.CruisePriceBundle | undefined): boolean {
  return bundle?.type === 'IN_FLOW_SELECTION'
}

export function hasValidCruiseBundleHotel(
  cruiseItem: App.Checkout.CruiseItem | undefined,
  items: Array<App.Checkout.AnyItem>,
): boolean {
  if (!cruiseItem?.startDate || !cruiseItem.occupancy) return false

  const checkOut = cruiseItem.startDate.substring(0, 10)
  const checkIn = subDays(new Date(checkOut), 1).toISOString().substring(0, 10)
  const occupancy = cruiseItem.occupancy

  return items.some(item => isBedbankItem(item) &&
    !!item.sessionId &&
    item.checkIn <= checkIn &&
    item.checkOut === checkOut &&
    item.occupancy.adults === occupancy.adults &&
    (item.occupancy.children ?? 0) === (occupancy.children ?? 0) &&
    (item.occupancy.infants ?? 0) === (occupancy.infants ?? 0) &&
    (item.occupancy.childrenAge ?? []).length === (occupancy.childrenAge ?? []).length &&
    (item.occupancy.childrenAge ?? []).every((age, index) => age === occupancy.childrenAge?.[index]),
  )
}

export function getCruiseBundleHotelOptions(
  offers: Array<App.HotelOffer | App.BedbankOffer>,
  ratesByOffer: App.State['offer']['bedbankOfferRates'],
  rateKey: string,
  useMemberPrice: boolean,
): Array<CruiseBundleHotelOption> {
  const options: Array<CruiseBundleHotelOption> = []

  for (const offer of offers) {
    if (!isBedbankOffer(offer)) continue

    const rates = (ratesByOffer[offer.id]?.[rateKey] ?? []).filter(rate => {
      const price = getBedbankAccommodationPrice(rate, useMemberPrice)
      return !rate.isFlightBundle && onlyRefundableRate(rate) && shouldShowBedbankRate(rate) &&
        rate.availableRooms !== 0 && rate.bedGroups.length > 0 && Number.isFinite(price) && price > 0
    })
    const rate = min(rates, rate => getBedbankAccommodationPrice(rate, useMemberPrice))
    if (!rate) continue

    options.push({ offer, rate, price: getBedbankAccommodationPrice(rate, useMemberPrice) })
  }

  return sortBy(options, option => option.price, 'asc')
}
